import type { TreeRow } from '../../interfaces/pieces/TreeRow';

type ChildComparator = (a: TreeRow, b: TreeRow) => number;

/**
 * Enfants affichés d'une ligne dépliable repliée ou non chargée : rsuite-table n'affiche le chevron que si
 * `children` est un tableau. Partagé et gelé, il ne contient jamais rien.
 */
const NO_VISIBLE_CHILDREN: TreeRow[] = Object.freeze(
	[],
) as unknown as TreeRow[];

/**
 * Copie d'une ligne réduite aux champs de TreeRow : rsuite-table ajoute aux lignes qu'il reçoit des clés Symbol
 * (parent, profondeur) que la décomposition `{ ...row }` recopierait, gardant en mémoire des arbres périmés.
 * `children` ne garde que l'information « dépliable » (tableau) ou non : les vrais enfants sont dans l'index.
 */
const toStoredRow = (row: TreeRow, isExpandable: boolean): TreeRow => ({
	identifiant: row.identifiant,
	libelle: row.libelle,
	rowKey: row.rowKey,
	nodeType: row.nodeType,
	...(row.bordereau !== undefined && { bordereau: row.bordereau }),
	...(row.mouvement !== undefined && { mouvement: row.mouvement }),
	...(row.exercice !== undefined && { exercice: row.exercice }),
	children: isExpandable ? NO_VISIBLE_CHILDREN : null,
});

/**
 * Indice d'insertion de `row` dans `keys` triées selon `compare` : après les lignes égales, comme le tri stable
 * de Array.sort le ferait pour une ligne ajoutée en fin de liste.
 */
const findInsertionIndex = (
	keys: readonly string[],
	rows: ReadonlyMap<string, TreeRow>,
	row: TreeRow,
	compare: ChildComparator,
): number => {
	let low = 0;
	let high = keys.length;
	while (low < high) {
		const middle = (low + high) >>> 1;
		if (compare(rows.get(keys[middle]), row) <= 0) {
			low = middle + 1;
		} else {
			high = middle;
		}
	}
	return low;
};

/**
 * Lectures communes à l'index et à son brouillon : une ligne, son parent, ses enfants, ses ancêtres, en O(1) par
 * pas (tables par rowKey).
 */
abstract class TreeIndexLecture {
	protected abstract get rootKeyList(): readonly string[];
	protected abstract get rowTable(): ReadonlyMap<string, TreeRow>;
	protected abstract get childTable(): ReadonlyMap<string, readonly string[]>;
	protected abstract get parentTable(): ReadonlyMap<string, string>;

	get size(): number {
		return this.rowTable.size;
	}

	has(rowKey: string): boolean {
		return this.rowTable.has(rowKey);
	}

	get(rowKey: string): TreeRow | undefined {
		return this.rowTable.get(rowKey);
	}

	getRoots(): TreeRow[] {
		return this.rootKeyList.map((key) => this.rowTable.get(key));
	}

	getParentKey(rowKey: string): string | undefined {
		return this.parentTable.get(rowKey);
	}

	getParent(rowKey: string): TreeRow | undefined {
		const parentKey = this.parentTable.get(rowKey);
		return parentKey === undefined ? undefined : this.rowTable.get(parentKey);
	}

	hasLoadedChildren(rowKey: string): boolean {
		return this.childTable.has(rowKey);
	}

	getChildKeys(rowKey: string): readonly string[] | undefined {
		return this.childTable.get(rowKey);
	}

	/** Enfants chargés d'une ligne, dans l'ordre, ou undefined s'ils ne sont pas chargés. */
	getChildren(rowKey: string): TreeRow[] | undefined {
		return this.childTable.get(rowKey)?.map((key) => this.rowTable.get(key));
	}

	/** Premier ancêtre (la ligne exclue) qui vérifie `predicate`. */
	findAncestor(
		rowKey: string,
		predicate: (row: TreeRow) => boolean,
	): TreeRow | undefined {
		let parentKey = this.parentTable.get(rowKey);
		while (parentKey !== undefined) {
			const parent = this.rowTable.get(parentKey);
			if (predicate(parent)) {
				return parent;
			}
			parentKey = this.parentTable.get(parentKey);
		}
		return undefined;
	}

	/** Ligne et toute sa descendance chargée, ligne comprise. */
	protected collectSubtree(rowKey: string): string[] {
		const keys = [rowKey];
		for (let i = 0; i < keys.length; i++) {
			const children = this.childTable.get(keys[i]);
			if (children) {
				keys.push(...children);
			}
		}
		return keys;
	}
}

/**
 * Brouillon d'un TreeIndex (TreeIndex.edit) : une série de modifications ne recopie chaque table qu'une fois, et
 * seulement si elle est modifiée (copie à la première écriture). Une mise à jour de ligne ne recopie que les
 * lignes, un replacement que les listes d'enfants ; une série sans effet ne recopie rien.
 */
export class TreeIndexDraft extends TreeIndexLecture {
	/** Vrai dès qu'une modification a changé le brouillon. */
	changed = false;

	private rootKeys: readonly string[];
	private rows: ReadonlyMap<string, TreeRow>;
	private childKeys: ReadonlyMap<string, readonly string[]>;
	private parentKeys: ReadonlyMap<string, string>;
	private readonly copied = new Set<'rows' | 'childKeys' | 'parentKeys'>();

	constructor(
		rootKeys: readonly string[],
		rows: ReadonlyMap<string, TreeRow>,
		childKeys: ReadonlyMap<string, readonly string[]>,
		parentKeys: ReadonlyMap<string, string>,
	) {
		super();
		this.rootKeys = rootKeys;
		this.rows = rows;
		this.childKeys = childKeys;
		this.parentKeys = parentKeys;
	}

	protected get rootKeyList() {
		return this.rootKeys;
	}

	protected get rowTable() {
		return this.rows;
	}

	protected get childTable() {
		return this.childKeys;
	}

	protected get parentTable() {
		return this.parentKeys;
	}

	/** Tables du brouillon, pour construire le nouvel index. */
	tables() {
		return {
			rootKeys: this.rootKeys,
			rows: this.rows,
			childKeys: this.childKeys,
			parentKeys: this.parentKeys,
		};
	}

	private writableRows(): Map<string, TreeRow> {
		if (!this.copied.has('rows')) {
			this.rows = new Map(this.rows);
			this.copied.add('rows');
		}
		this.changed = true;
		return this.rows as Map<string, TreeRow>;
	}

	private writableChildKeys(): Map<string, readonly string[]> {
		if (!this.copied.has('childKeys')) {
			this.childKeys = new Map(this.childKeys);
			this.copied.add('childKeys');
		}
		this.changed = true;
		return this.childKeys as Map<string, readonly string[]>;
	}

	private writableParentKeys(): Map<string, string> {
		if (!this.copied.has('parentKeys')) {
			this.parentKeys = new Map(this.parentKeys);
			this.copied.add('parentKeys');
		}
		this.changed = true;
		return this.parentKeys as Map<string, string>;
	}

	/** Oublie des lignes (rows, enfants, parent), sans toucher à la liste de leur parent. */
	private forget(keys: string[]): void {
		if (!keys.length) return;
		const rows = this.writableRows();
		const childKeys = this.writableChildKeys();
		const parentKeys = this.writableParentKeys();
		keys.forEach((key) => {
			rows.delete(key);
			childKeys.delete(key);
			parentKeys.delete(key);
		});
	}

	/** Retire une ligne de la liste de son parent (ou des racines), sans supprimer sa descendance. */
	private detach(rowKey: string): void {
		const parentKey = this.parentKeys.get(rowKey);
		if (parentKey === undefined) {
			this.rootKeys = this.rootKeys.filter((key) => key !== rowKey);
			this.changed = true;
			return;
		}
		const siblings = this.childKeys.get(parentKey);
		if (siblings) {
			this.writableChildKeys().set(
				parentKey,
				siblings.filter((key) => key !== rowKey),
			);
		}
	}

	/** Remplace la liste des racines (construction de l'index). */
	setRootKeys(rootKeys: readonly string[]): void {
		this.rootKeys = rootKeys;
		this.changed = true;
	}

	/**
	 * Insère des lignes sous `parentKey` (null : racines), avec leurs enfants non vides (chargés). Une ligne déjà
	 * présente ailleurs dans l'arbre (pièce passée d'un bordereau à un autre) en est d'abord retirée, avec son
	 * ancienne descendance : une ligne n'a jamais deux parents.
	 */
	insertRows(list: TreeRow[], parentKey: string | null): void {
		list.forEach((row) => {
			if (this.rows.has(row.rowKey)) {
				if (this.parentKeys.get(row.rowKey) !== (parentKey ?? undefined)) {
					this.detach(row.rowKey);
				}
				this.forget(this.collectSubtree(row.rowKey));
			}
			const isExpandable = Array.isArray(row.children);
			this.writableRows().set(row.rowKey, toStoredRow(row, isExpandable));
			if (parentKey !== null) {
				this.writableParentKeys().set(row.rowKey, parentKey);
			}
			if (isExpandable && row.children.length > 0) {
				this.writableChildKeys().set(
					row.rowKey,
					row.children.map((child) => child.rowKey),
				);
				this.insertRows(row.children, row.rowKey);
			}
		});
	}

	/** Remplace les enfants d'une ligne (et leur descendance) par `children`, qui deviennent les enfants chargés. */
	setChildren(parentKey: string, children: TreeRow[]): void {
		if (!this.rows.has(parentKey)) {
			return;
		}
		this.forget(this.collectSubtree(parentKey).slice(1));
		this.insertRows(children, parentKey);
		this.writableChildKeys().set(
			parentKey,
			children.map((child) => child.rowKey),
		);
	}

	/** Libère les enfants chargés d'une ligne et leur descendance : ils seront rechargés au prochain dépliage. */
	unloadChildren(parentKey: string): void {
		if (!this.childKeys.has(parentKey)) {
			return;
		}
		this.forget(this.collectSubtree(parentKey).slice(1));
		this.writableChildKeys().delete(parentKey);
	}

	/**
	 * Met à jour une ligne. Ses enfants ne changent pas : le `children` éventuellement renvoyé par `updater` (copie
	 * de ligne venue de rsuite-table, par exemple) est ignoré.
	 */
	updateNode(rowKey: string, updater: (row: TreeRow) => TreeRow): void {
		const row = this.rows.get(rowKey);
		if (!row) {
			return;
		}
		this.writableRows().set(
			rowKey,
			toStoredRow({ ...updater(row), rowKey }, Array.isArray(row.children)),
		);
	}

	/** Supprime une ligne et toute sa descendance. */
	deleteNode(rowKey: string): void {
		if (!this.rows.has(rowKey)) {
			return;
		}
		this.detach(rowKey);
		this.forget(this.collectSubtree(rowKey));
	}

	/**
	 * Ajoute une ligne aux enfants chargés de `parentKey`, à sa place selon `compare` (en fin de liste sans
	 * comparateur). Une ligne déjà présente ailleurs est déplacée avec sa descendance. Sans effet si les enfants
	 * du parent ne sont pas chargés : la ligne arrivera avec eux.
	 */
	addChild(parentKey: string, row: TreeRow, compare?: ChildComparator): void {
		if (!this.childKeys.has(parentKey)) {
			return;
		}
		const former = this.rows.get(row.rowKey);
		if (former) {
			this.detach(row.rowKey);
			this.writableRows().set(
				row.rowKey,
				toStoredRow(row, Array.isArray(former.children)),
			);
			this.writableParentKeys().set(row.rowKey, parentKey);
		} else {
			this.insertRows([row], parentKey);
		}

		const siblings = this.childKeys
			.get(parentKey)
			.filter((key) => key !== row.rowKey);
		const position = compare
			? findInsertionIndex(
					siblings,
					this.rows,
					this.rows.get(row.rowKey),
					compare,
				)
			: siblings.length;
		this.writableChildKeys().set(parentKey, [
			...siblings.slice(0, position),
			row.rowKey,
			...siblings.slice(position),
		]);
	}

	/** Replace une ligne parmi ses sœurs selon `compare`, après une modification qui change son ordre. */
	reposition(rowKey: string, compare: ChildComparator): void {
		const parentKey = this.parentKeys.get(rowKey);
		if (parentKey === undefined) {
			return;
		}
		const siblings = this.childKeys
			.get(parentKey)
			.filter((key) => key !== rowKey);
		const position = findInsertionIndex(
			siblings,
			this.rows,
			this.rows.get(rowKey),
			compare,
		);
		this.writableChildKeys().set(parentKey, [
			...siblings.slice(0, position),
			rowKey,
			...siblings.slice(position),
		]);
	}
}

/**
 * Arbre normalisé et immuable de la treeview : chaque ligne est indexée par son rowKey, avec la liste ordonnée de
 * ses enfants et son parent. Les recherches (ligne, parent, enfants) sont en O(1), au lieu d'un parcours de tout
 * l'arbre. Chaque modification renvoie un nouvel index ; une série de modifications se fait dans un brouillon
 * (edit), qui ne recopie que les tables modifiées, une seule fois. Les lignes non modifiées restent partagées.
 *
 * Une ligne dont les enfants ne sont pas chargés n'a pas d'entrée dans `childKeys` : `hasLoadedChildren` permet
 * de savoir s'il faut les charger au dépliage.
 */
class TreeIndex extends TreeIndexLecture {
	static readonly EMPTY = new TreeIndex([], new Map(), new Map(), new Map());

	private constructor(
		private readonly rootKeys: readonly string[],
		private readonly rows: ReadonlyMap<string, TreeRow>,
		private readonly childKeys: ReadonlyMap<string, readonly string[]>,
		private readonly parentKeys: ReadonlyMap<string, string>,
	) {
		super();
	}

	protected get rootKeyList() {
		return this.rootKeys;
	}

	protected get rowTable() {
		return this.rows;
	}

	protected get childTable() {
		return this.childKeys;
	}

	protected get parentTable() {
		return this.parentKeys;
	}

	/**
	 * Index construit à partir des lignes racines. Les `children` non vides d'une ligne sont considérés comme
	 * chargés ; un tableau vide signifie « dépliable, enfants à charger ».
	 */
	static fromRoots(roots: TreeRow[]): TreeIndex {
		return TreeIndex.EMPTY.edit((draft) => {
			draft.insertRows(roots, null);
			draft.setRootKeys(roots.map((root) => root.rowKey));
		});
	}

	/**
	 * Applique une série de modifications dans un brouillon, qui ne recopie que les tables modifiées. Renvoie
	 * l'index lui-même si rien n'a changé.
	 */
	edit(recipe: (draft: TreeIndexDraft) => void): TreeIndex {
		const draft = new TreeIndexDraft(
			this.rootKeys,
			this.rows,
			this.childKeys,
			this.parentKeys,
		);
		recipe(draft);
		if (!draft.changed) {
			return this;
		}
		const { rootKeys, rows, childKeys, parentKeys } = draft.tables();
		return new TreeIndex(rootKeys, rows, childKeys, parentKeys);
	}

	/** Remplace les enfants d'une ligne (cf. TreeIndexDraft.setChildren). */
	setChildren(parentKey: string, children: TreeRow[]): TreeIndex {
		return this.edit((draft) => draft.setChildren(parentKey, children));
	}

	/** Libère les enfants chargés d'une ligne (cf. TreeIndexDraft.unloadChildren). */
	unloadChildren(parentKey: string): TreeIndex {
		return this.edit((draft) => draft.unloadChildren(parentKey));
	}

	/** Met à jour une ligne, sans toucher à ses enfants (cf. TreeIndexDraft.updateNode). */
	updateNode(rowKey: string, updater: (row: TreeRow) => TreeRow): TreeIndex {
		return this.edit((draft) => draft.updateNode(rowKey, updater));
	}

	/** Supprime une ligne et toute sa descendance. */
	deleteNode(rowKey: string): TreeIndex {
		return this.edit((draft) => draft.deleteNode(rowKey));
	}

	/** Ajoute une ligne aux enfants chargés de `parentKey`, à sa place (cf. TreeIndexDraft.addChild). */
	addChild(
		parentKey: string,
		row: TreeRow,
		compare?: ChildComparator,
	): TreeIndex {
		return this.edit((draft) => draft.addChild(parentKey, row, compare));
	}

	/** Replace une ligne parmi ses sœurs selon `compare`. */
	reposition(rowKey: string, compare: ChildComparator): TreeIndex {
		return this.edit((draft) => draft.reposition(rowKey, compare));
	}

	/**
	 * Arbre imbriqué attendu par rsuite-table, réduit aux lignes visibles : seuls les enfants des lignes dépliées
	 * y figurent. rsuite-table aplatit et recopie tout l'arbre qu'il reçoit à chaque dépliage : lui passer les
	 * enfants des lignes repliées rendrait chaque clic proportionnel à tout ce qui a été chargé.
	 *
	 * @param acceptChild filtre des enfants affichés d'une ligne (filtres de la treeview)
	 */
	buildVisibleTree(
		expandedKeys: ReadonlySet<string>,
		acceptChild?: (parent: TreeRow, child: TreeRow) => boolean,
	): TreeRow[] {
		const build = (rowKey: string): TreeRow => {
			const row = this.rows.get(rowKey);
			const children = this.childKeys.get(rowKey);
			if (!children || !expandedKeys.has(rowKey)) {
				return row;
			}
			const visibleChildren: TreeRow[] = [];
			children.forEach((childKey) => {
				const child = this.rows.get(childKey);
				if (!acceptChild || acceptChild(row, child)) {
					visibleChildren.push(build(childKey));
				}
			});
			return { ...row, children: visibleChildren };
		};

		return this.rootKeys.map(build);
	}
}

export type { TreeIndexLecture };
export default TreeIndex;
